import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type MenuItem = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
};

interface ComboButtonProps {
  label: string;
  onPrimaryClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  menuItems?: MenuItem[];
  size?: "sm" | "default" | "lg";
  variant?: "default" | "outline" | "secondary" | "destructive" | "ghost" | "link";
  className?: string;
  includePrimaryInMenu?: boolean;
}

export function ComboButton({
  label,
  onPrimaryClick,
  icon,
  disabled = false,
  menuItems = [],
  size = "sm",
  variant = "outline",
  className,
  includePrimaryInMenu = true,
}: ComboButtonProps) {
  const dividerClass = variant === "default" ? "divide-white/25" : "divide-border";
  const isOutline = variant === "outline";
  return (
    <div
      className={cn(
        "inline-flex items-stretch rounded-md overflow-hidden divide-x",
        dividerClass,
        isOutline && "border bg-white",
        // Group focus ring (consistent with inputs)
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        className
      )}
    >
      <Button
        onClick={onPrimaryClick}
        disabled={disabled}
        size={size}
        variant={variant}
        className={cn(
          "rounded-none min-h-[44px] border-0 shadow-none",
          isOutline && "bg-transparent",
          disabled && "cursor-not-allowed"
        )}
      >
        {icon}
        {icon ? <span className="ml-2">{label}</span> : label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size={size}
            variant={variant}
            className={cn(
              "rounded-none px-2 min-w-[40px] shrink-0 min-h-[44px] border-0 shadow-none",
              isOutline && "bg-transparent",
              disabled && "cursor-not-allowed"
            )}
            disabled={disabled}
            aria-label="More actions"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        {(includePrimaryInMenu || menuItems.length > 0) && (
          <DropdownMenuContent align="end" className="w-[220px]">
            {includePrimaryInMenu && (
              <DropdownMenuItem onClick={onPrimaryClick} disabled={disabled}>
                {icon}
                {icon ? <span className="ml-2">{label}</span> : label}
              </DropdownMenuItem>
            )}
            {menuItems.map((item, idx) => (
              <DropdownMenuItem
                key={idx}
                onClick={item.onClick}
                disabled={item.disabled || disabled}
              >
                {item.icon}
                {item.icon ? <span className="ml-2">{item.label}</span> : item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    </div>
  );
}
